/**
 * 환경설정 &gt; 외부메일 (migration 164, 2026-09-05 사용자 지시).
 *
 * 목록 한 벌 — 받기방식 · 포트 · 보안연결(SSL) · 아이디 · 저장메일함 · 원본삭제여부 · 관리.
 *
 * ★★★ **개인설정에 있던 「외부 메일 계정」을 이 탭으로 옮겼다**(사용자 지시).
 *    표(`external_mail_accounts`)는 그대로다 — **화면만** 옮겼으므로 등록해 둔 계정은
 *    여기서 그대로 보인다. 옛 자리에는 어디로 갔는지 한 줄을 남겼다(찾다가 못 찾으면 안 된다).
 * ★★ 목록은 **즉시 반영**이다(추가·수정·삭제는 제출이 아니다) — 저장바를 두지 않는다.
 * ★  IMAP 도 이제 실제로 가져온다(2026-09-07) — 줄에 붙어 있던 「아직 안 가져옴」을 걷어냈다.
 *    안내를 걷어낼 때는 **그 안내를 지키던 검사도 함께** 뒤집는다(초록인 거짓이 남지 않게).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createMailAccount, deleteMailAccount, listMailAccounts, patchMailAccount,
  type MailAccount, type MailAccountInput,
} from '../../api/external-mail';
import { listMailFolders, type MailFolderNode } from '../../api/mail';
import { Edit3, Plus } from '../../icons';
import { Button } from '../../components/shared/Button';
import { Table, type TableColumn } from '../../components/shared/Table';
import { tMail, type MailI18nKey } from '../../lib/mail-i18n';
import { MailConfirmDialog } from './MailConfirmDialog';
import { MailExternalAccountModal } from './MailExternalAccountModal';

/**
 * 마지막 회차 시각 — 오늘이면 시:분, 아니면 월/일 시:분.
 *
 * ★ 상대 시각("3분 전")은 다시 그리지 않으면 곧 거짓말이 된다. 이 목록은 스스로 다시 그리는
 *   자리가 아니므로 **적힌 그대로 참인** 절대 시각을 쓴다.
 */
export function formatSyncAt(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const sameDay = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? hhmm : `${d.getMonth() + 1}/${d.getDate()} ${hhmm}`;
}

type Ask = { kind: 'one'; row: MailAccount } | { kind: 'all' };

export function MailSettingsExternal() {
  const [rows, setRows] = useState<MailAccount[]>([]);
  const [folders, setFolders] = useState<MailFolderNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [editFor, setEditFor] = useState<{ row: MailAccount | null } | null>(null);
  const [ask, setAsk] = useState<Ask | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalErr, setModalErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [accounts, f] = await Promise.all([listMailAccounts(), listMailFolders()]);
    setRows(accounts);
    setFolders(f);
  }, []);

  useEffect(() => {
    let cancelled = false;
    load()
      .catch(() => { if (!cancelled) setNote({ ok: false, text: tMail('mail.ext.load_failed') }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [load]);

  const run = useCallback(async (
    fn: () => Promise<void>, failKey: MailI18nKey, inModal = false,
  ): Promise<boolean> => {
    setBusy(true);
    setModalErr(null);
    setNote(null);
    try {
      await fn();
      await load();
      setNote({ ok: true, text: tMail('mail.ext.saved') });
      return true;
    } catch (err) {
      const res = (err as { response?: { status?: number; data?: { code?: string; message?: string } } }).response;
      const msg = res?.data?.code === 'ALREADY_EXISTS' ? tMail('mail.ext.err_exists')
        : res?.data?.message ?? tMail(failKey);
      if (inModal) setModalErr(msg); else setNote({ ok: false, text: msg });
      return false;
    } finally {
      setBusy(false);
    }
  }, [load]);

  const folderName = useCallback((id: string | null | undefined): string => {
    if (!id) return tMail('mail.ext.inbox');
    const hit = folders.find((f) => f.id === id);
    // 메일함을 지우면 서버가 NULL 로 돌리지만, 목록을 아직 안 받아온 사이라면 이렇게 보인다.
    return hit ? hit.name : tMail('mail.ext.folder_gone');
  }, [folders]);

  const columns: ReadonlyArray<TableColumn<MailAccount>> = useMemo(() => [
    {
      key: 'account',
      header: tMail('mail.ext.col.account'),
      rowHeader: true,
      render: (r) => <span className="filtrule__nm">{r.email}</span>,
    },
    {
      key: 'recv',
      header: tMail('mail.ext.col.recv'),
      width: '110px',
      render: (r) => (
        <span className="extacc__proto">
          <span>{r.recvProtocol.toUpperCase()}</span>
        </span>
      ),
    },
    {
      key: 'port', header: tMail('mail.ext.col.port'), numeric: true, width: '80px',
      render: (r) => <span>{r.recvPort ?? '—'}</span>,
    },
    {
      key: 'secure', header: tMail('mail.ext.col.secure'), width: '110px',
      render: (r) => <span>{tMail(r.recvSecure ? 'mail.ext.yes' : 'mail.ext.no')}</span>,
    },
    {
      key: 'user', header: tMail('mail.ext.col.user'),
      render: (r) => <span className="filtrule__nm">{r.recvUser ?? '—'}</span>,
    },
    {
      /*
       * ★★★ 마지막 가져오기 (2026-09-07).
       *
       * 서버는 회차마다 `lastSyncAt`·`lastSyncCount`·`lastSyncError` 를 남기고 있었는데
       * **화면이 그걸 한 번도 안 그렸다.** 그래서 네이버 계정이 몇 주 동안 인증 실패로 0통인
       * 채였는데 아무도 몰랐다 — 「왜 안 들어오지」를 물을 자리조차 없었다.
       * ★★ 「한 번도 안 돌았다」와 「돌았는데 0통」은 **다르다**. 둘을 같은 말로 그리면
       *   설정이 틀린 것인지 그냥 새 메일이 없는 것인지 못 가른다.
       */
      key: 'lastsync',
      header: tMail('mail.ext.col.lastsync'),
      width: '150px',
      render: (r) => {
        if (r.lastSyncError) {
          // 칸이 좁다 — 줄에는 짧게, 온전한 사유는 title 로.
          return (
            <span className="extacc__warn" title={r.lastSyncError}>
              {tMail('mail.ext.sync_failed')}
            </span>
          );
        }
        if (!r.lastSyncAt) return <span className="extacc__muted">{tMail('mail.ext.sync_never')}</span>;
        return <span>{formatSyncAt(r.lastSyncAt)}</span>;
      },
    },
    {
      key: 'folder', header: tMail('mail.ext.col.folder'), width: '140px',
      render: (r) => <span>{folderName(r.targetFolderId)}</span>,
    },
    {
      key: 'delorig', header: tMail('mail.ext.col.delorig'), width: '120px',
      render: (r) => (
        <span className={r.deleteOriginal ? 'extacc__on' : undefined}>
          {tMail(r.deleteOriginal ? 'mail.ext.yes' : 'mail.ext.no')}
        </span>
      ),
    },
    {
      key: 'manage', header: tMail('mail.ext.col.manage'), width: '170px',
      render: (r) => (
        <span className="mbox__acts">
          <Button
            variant="secondary" size="sm" disabled={busy}
            iconLeft={<Edit3 size={13} aria-hidden />}
            onClick={() => { setModalErr(null); setEditFor({ row: r }); }}
          >
            {tMail('mail.ext.edit')}
          </Button>
          <Button
            variant="quiet-danger" size="sm" disabled={busy}
            onClick={() => setAsk({ kind: 'one', row: r })}
          >
            {tMail('mail.ext.delete')}
          </Button>
        </span>
      ),
    },
  ], [busy, folderName]);

  if (loading) return <div className="wc-set__body"><p className="wc-set__hint">…</p></div>;

  return (
    <div className="wc-set__body">
      <section className="wc-set__sec" aria-labelledby="mext-h">
        <div className="wc-set__sechead">
          <h2 className="wc-set__sech" id="mext-h">{tMail('mail.ext.sec.list')}</h2>
          <Button
            variant="primary" size="sm"
            iconLeft={<Plus size={14} aria-hidden />}
            onClick={() => { setModalErr(null); setEditFor({ row: null }); }}
          >
            {tMail('mail.ext.add')}
          </Button>
          <Button
            variant="quiet-danger" size="sm"
            disabled={rows.length === 0 || busy}
            onClick={() => setAsk({ kind: 'all' })}
          >
            {tMail('mail.ext.clear')}
          </Button>
        </div>

        <Table
          caption={tMail('mail.ext.sec.list')}
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          empty={<span>{tMail('mail.ext.none')}</span>}
          wrapClassName="mbox__tablewrap"
        />
        <p className="wc-set__hint">{tMail('mail.ext.moved_note')}</p>
        {note && (
          <p className={note.ok ? 'wc-set__ok' : 'wc-set__bad'} role="status">{note.text}</p>
        )}
      </section>

      {editFor && (
        <MailExternalAccountModal
          target={editFor.row}
          folders={folders}
          busy={busy}
          error={modalErr}
          onClose={() => { setEditFor(null); setModalErr(null); }}
          onSubmit={(input: MailAccountInput) => {
            void (async () => {
              const editing = editFor.row;
              const ok = await run(async () => {
                const res = editing
                  ? await patchMailAccount(editing.id, input)
                  : await createMailAccount(input);
                /* ★ 저장은 됐는데 **연결이 안 되는** 상태를 조용히 넘기지 않는다 —
                     서버가 등록 직후 한 번 확인해 그 결과를 함께 준다. */
                const bad = [res.recv?.ok === false ? res.recv.message : null,
                  res.smtp?.ok === false ? res.smtp.message : null].filter(Boolean).join(' / ');
                if (bad) setNote({ ok: false, text: tMail('mail.ext.probe_bad').replace('{m}', bad) });
              }, 'mail.ext.err_failed', true);
              if (ok) setEditFor(null);
            })();
          }}
        />
      )}

      <MailConfirmDialog
        open={ask !== null}
        danger
        busy={busy}
        title={ask?.kind === 'one'
          ? tMail('mail.ext.del_title').replace('{n}', ask.row.email)
          : tMail('mail.ext.clear_title')}
        description={tMail('mail.ext.del_desc')}
        confirmLabel={tMail('mail.ext.delete')}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          const cur = ask;
          if (!cur) return;
          void (async () => {
            const ok = await run(async () => {
              if (cur.kind === 'one') await deleteMailAccount(cur.row.id);
              // 전체삭제는 서버에 길이 없다 — 줄마다 지운다(개수가 몇 개 안 된다).
              else for (const r of rows) await deleteMailAccount(r.id);
            }, 'mail.ext.err_failed');
            if (ok) setAsk(null);
          })();
        }}
      />
    </div>
  );
}
